import { ReactNode, FC, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { cloneDeep } from 'lodash';
import { LoadState, isUUID } from '@/lib/helpers';
import { ShortcutMessage, ShortcutModel } from '@/lib/models/shortcut.model';
import useApp from '@/hooks/use-app.hook';
import { getThreadById, makeDeckMessage } from '@/lib/services/thread.service';
import { ThreadStatus, ThreadContext, ThreadError } from './helpers';
import useThreadToast from '@/hooks/use-thread-toast.hook';
import {
  StreamControlSignal,
  StreamMessage,
  stopStreamClient,
  streamUserMessage,
} from '@/lib/services/thread-stream.service';
import useUserSettings from '@/hooks/use-user-settings.hook';
import { ThreadModel } from '@/lib/models/thread/thread.model';
import { ThreadMessageModel } from '@/lib/models/thread/thread-message.model';
import { ThreadContext as ThreadContextMap } from '@/lib/models/thread';
import { getTimestamp } from '@/lib/date-helpers';
import useFeature from '@/hooks/use-feature.hook';
import { ThreadSettingsModel } from '@/lib/models/thread/thread-settings.model';
import useEmitter from '@/hooks/use-emitter.hook';
import { useSearch } from '@tanstack/react-router';
import { TileModel } from '@/lib/models/user-settings/tile.model';
import { useStreamBuffer } from '@/hooks/use-stream-buffer';

interface Props {
  children: ReactNode;
}

const getUrlThreadId = (): string => {
  const threadId = String(window.location.pathname).split('/').pop() || '';
  return isUUID(threadId) ? threadId : '';
};

export const ThreadProvider: FC<Props> = ({ children }) => {
  const [thread, setThread] = useState<ThreadModel>(new ThreadModel({}));
  const [loadState, setLoadState] = useState<LoadState>('unloaded');
  const [threadLoadState, setThreadLoadState] = useState<LoadState>('unloaded');
  const [messageStatus, setMessageStatus] = useState<ThreadStatus>('open');
  const [controlSignal, setControlSignal] = useState<StreamControlSignal | null>(null);
  const [working, setWorking] = useState(false);
  const [streamContent, setStreamContent] = useState('');

  const pendingStreamThreadMessageRef = useRef<ThreadMessageModel | null>(null);
  const pendingUserMessageRef = useRef<ThreadMessageModel | null>(null);

  const {
    feed: streamBufferFeed,
    stop: streamBufferStop,
    flushComplete: streamBufferFlushComplete,
  } = useStreamBuffer({
    onUpdate: setStreamContent,
    onComplete: useCallback(() => {
      const streamThreadMessage = pendingStreamThreadMessageRef.current;
      const userMessage = pendingUserMessageRef.current;
      if (streamThreadMessage && userMessage) {
        setThread((old) => {
          const updated = cloneDeep(old);
          updated.conversation.push(streamThreadMessage);
          const userMessageIndex = updated.conversation.findIndex(
            ({ id }) => id === userMessage.id
          );
          if (userMessageIndex > -1) {
            updated.conversation[userMessageIndex].onDeck = false;
          }
          return updated;
        });
        setMessageStatus('open');
        setStreamContent('');
      }
      pendingStreamThreadMessageRef.current = null;
      pendingUserMessageRef.current = null;
    }, []),
  });

  const { assistant_id: qsAgentId = '' } = useSearch({ strict: false });
  const [settings, setSettings] = useState<ThreadSettingsModel>(
    new ThreadSettingsModel({ agentId: qsAgentId })
  );

  const { signedIn } = useApp();
  const { canViewHistory } = useFeature();
  const { toast } = useThreadToast();
  const {
    threadSettings: userThreadSettings,
    agents,
    datasources,
    privateModelSettings,
    toggleRecent,
  } = useUserSettings();
  const { emit } = useEmitter();

  const hasOnDeckShortcut = useMemo(() => {
    return thread.conversation.some(({ onDeck, shortcut }) => onDeck && !!shortcut);
  }, [thread]);

  /*
   * Copy settings from a thread that was just loaded into the thread settings
   */
  const syncFromThread = useCallback(
    (thread: ThreadModel, agentId?: string) => {
      setSettings((old) => {
        const updated = new ThreadSettingsModel(old._props);

        // Validate the loaded thread agent id against current user setting
        const compAgentId = agentId || thread.latestAgentId;
        const validAgent = agents.find(({ id }) => id === compAgentId);
        if (validAgent) {
          updated.agentId = compAgentId;
        } else {
          const defaultAgent = agents.find(({ isDefaultAgent }) => isDefaultAgent);
          updated.agentId = defaultAgent?.id || '';
        }

        // Validate the loaded thread model id against current user settings
        const validModel = privateModelSettings.availablePrivateModels.find(
          ({ id }) => id === thread.latestPrivateModelId
        );
        updated.privateModelId = validModel?.id || '';

        // Validate thread datasource settings by iterating instead through available datasources
        updated.datasources = [];
        datasources.forEach(({ id, isPublicInternet }) => {
          // mentioned by the thread? then it's enabled
          updated.datasources.push({
            id,
            isPublicInternet,
            enabled: thread.latestDatasourceIds.includes(id),
          });
        });

        return updated;
      });
    },
    [datasources, agents, privateModelSettings]
  );

  /*
   * Copy user default setting into to the current thread settings
   */
  const syncFromUserSettings = useCallback(
    (agentId?: string) => {
      const { privateModelId: userPrivateModelId, agentId: userAgentId } = userThreadSettings;

      setSettings((old) => {
        const updated = new ThreadSettingsModel(cloneDeep(old._props));
        updated.privateModelId = userPrivateModelId;
        updated.agentId = agentId || userAgentId;
        updated.datasources = [];
        userThreadSettings.datasources.forEach((datasource) => {
          if (!updated.isDefaultAgent && datasource.isPublicInternet) {
            return;
          }
          updated.datasources.push(cloneDeep(datasource));
        });

        return updated;
      });
    },
    [userThreadSettings]
  );

  const loadThread = useCallback(
    async (threadId?: string, agentId?: string): Promise<boolean> => {
      if (threadLoadState === 'loading') {
        return false;
      }

      if (messageStatus !== 'open') {
        toast(ThreadError.SendingSwitchThread);
        return false;
      }

      if (hasOnDeckShortcut) {
        toast(ThreadError.WorkingSwitchThread);
        return false;
      }

      let thread: ThreadModel | null = null;

      setThreadLoadState('loading');

      if (threadId) {
        thread = await getThreadById(threadId);
      } else {
        thread = new ThreadModel({
          agentId,
        });
      }

      if (!thread) {
        toast(ThreadError.NoDisassemble, 'error');
        return false;
      }

      setThread(thread);

      if (threadId) {
        syncFromThread(thread, agentId);
      } else {
        syncFromUserSettings(agentId);
      }

      setThreadLoadState('loaded');

      return true;
    },
    [threadLoadState, messageStatus, hasOnDeckShortcut, toast, syncFromThread, syncFromUserSettings]
  );

  const addMessage = useCallback((message: ThreadMessageModel) => {
    setThread((old) => {
      const updated = cloneDeep(old);
      updated.conversation.push(message);
      return updated;
    });
  }, []);

  const removeMessage = useCallback((message: ThreadMessageModel) => {
    const { id: messageId } = message;
    setThread((old) => {
      const updated = cloneDeep(old);
      const messageIndex = updated.conversation.findIndex(({ id }) => id === messageId);
      if (messageIndex > -1) {
        updated.conversation.splice(messageIndex, 1);
      }
      return updated;
    });
  }, []);

  const streamMessage = useCallback(
    (message: ThreadMessageModel) => {
      const onMessage = ({
        error,
        errorCode,
        content,
        isComplete,
        threadId: streamThreadId,
        threadMessage: streamThreadMessage,
      }: StreamMessage) => {
        if (streamThreadId !== thread.id) {
          // @todo abort the stream, yikes
          streamBufferStop();
          pendingStreamThreadMessageRef.current = null;
          pendingUserMessageRef.current = null;
          setMessageStatus('open');
          return;
        }

        if (error) {
          // remove the message, it did not send
          removeMessage(message);

          emit('stream-error', { message });

          streamBufferStop();
          pendingStreamThreadMessageRef.current = null;
          pendingUserMessageRef.current = null;
          setMessageStatus('open');

          if (errorCode === 401) {
            toast(ThreadError.SessionExpired, 'warning');
            return;
          }

          toast(ThreadError.NoDisassemble, 'error');
          return;
        }

        setMessageStatus('stream');
        streamBufferFeed(content);

        if (isComplete) {
          if (!streamThreadMessage) {
            toast(ThreadError.NoDisassemble, 'error');
            streamBufferStop();
            setMessageStatus('open');
            return;
          }

          pendingStreamThreadMessageRef.current = streamThreadMessage;
          pendingUserMessageRef.current = message;
          streamBufferFlushComplete();
        }
      };

      const onContext = (threadContext: ThreadContextMap) => {
        setThread((old) => {
          const updated = cloneDeep(old);
          updated.context = threadContext;
          return updated;
        });
      };

      const onControl = (controlSignal: StreamControlSignal | null) => {
        setControlSignal(controlSignal || null);
      };

      message.agentId = settings.agentId;
      message.privateModelId = settings.privateModelId;

      message.datasourceIds = settings.datasources
        .filter(({ enabled }) => enabled)
        .map(({ id }) => id);

      streamUserMessage(message, onMessage, onContext, onControl);

      toggleRecent(
        new TileModel({
          tileType: 'conversation',
          resourceId: message.threadId,
          serviceId: message.agentId,
        })
      );
    },
    [
      thread.id,
      settings,
      toast,
      removeMessage,
      emit,
      toggleRecent,
      streamBufferFeed,
      streamBufferStop,
      streamBufferFlushComplete,
    ]
  );

  const closeStream = useCallback(() => {
    stopStreamClient();
    streamBufferStop();
    pendingStreamThreadMessageRef.current = null;
    pendingUserMessageRef.current = null;
    setMessageStatus('open');
  }, [streamBufferStop]);

  const loadShortcut = useCallback(
    (shortcut: ShortcutModel) => {
      if (!thread.id) {
        toast(ThreadError.NotFoundThread, 'error');
        return false;
      }

      if (messageStatus !== 'open') {
        toast(ThreadError.SendingLoadShortcut);
        return false;
      }

      if (thread.locked) {
        return false;
      }

      // If there is already a shortcut in the thread, but the user has not yet filled out anything, simply replace it with this one being loaded
      const existIndex = thread.conversation.findIndex(
        ({ onDeck, shortcut }) => onDeck && shortcut
      );
      let removeIndex = -1;

      if (existIndex > -1) {
        // user is already working on an on-deck shortcut, don't replace it
        if (working) {
          toast(ThreadError.WorkingLoadShortcut);
          return false;
        }
        removeIndex = existIndex;
      }

      const deckMessage = makeDeckMessage(thread.id, 'user', '', shortcut.message);

      setThread((old) => {
        const updated = cloneDeep(old);
        if (removeIndex > -1) {
          updated.conversation.splice(removeIndex, 1);
        }

        updated.conversation.push(deckMessage);

        return updated;
      });

      return true;
    },
    [thread, working, messageStatus, toast]
  );

  const removeDeckMessage = useCallback((messageId: string) => {
    setThread((old) => {
      const updated = cloneDeep(old);
      const messageIndex = updated.conversation.findIndex(({ id }) => id === messageId);

      if (messageIndex > -1) {
        updated.conversation.splice(messageIndex, 1);
      }

      return updated;
    });

    return true;
  }, []);

  const sendDeckMessage = useCallback(
    (message: ThreadMessageModel) => {
      if (!thread.id) {
        toast(ThreadError.NotFoundThread, 'error');
        return false;
      }

      if (messageStatus !== 'open') {
        toast(ThreadError.Sending);
        return false;
      }

      const updatedThread = cloneDeep(thread);
      const messageIndex = updatedThread.conversation.findIndex(({ id }) => id === message.id);
      if (messageIndex === -1) {
        toast(ThreadError.NotFoundMessage, 'error');
        return false;
      }

      if (thread.locked) {
        return false;
      }

      setMessageStatus('send');

      const { shortcut } = message;

      // mark the on-deck message as having been sent
      setThread((old) => {
        const updated = cloneDeep(old);
        updated.conversation[messageIndex].onDeck = false;
        updated.conversation[messageIndex].shortcut = shortcut;
        updated.conversation[messageIndex].timestamp = getTimestamp(new Date(), true) || 0;
        return updated;
      });

      streamMessage(message);

      return true;
    },
    [thread, messageStatus, toast, streamMessage]
  );

  /*
   * Given text content that the user enters via MessageControl, create a new thread message, send
   * the message off for inference, then create a new thread message with the response.
   */
  const sendMessage = useCallback(
    (content: string, shortcut?: ShortcutMessage) => {
      if (!thread.id) {
        toast(ThreadError.NotFoundThread, 'error');
        return false;
      }

      if (messageStatus !== 'open') {
        toast(ThreadError.Sending);
        return false;
      }

      if (hasOnDeckShortcut) {
        toast(ThreadError.Working);
        return false;
      }

      if (thread.locked) {
        return false;
      }

      setMessageStatus('send');

      const deckMessage = makeDeckMessage(thread.id, 'user', content, shortcut);

      // because we are sending the message right away, lock the shortcut form by marking it as no longer on-deck
      if (shortcut) {
        deckMessage.onDeck = false;
      }

      addMessage(deckMessage);

      // using sendDeckMessage here would create consistency, but how to work with the recently updated thread?

      streamMessage(deckMessage);

      return true;
    },
    [messageStatus, hasOnDeckShortcut, thread, toast, addMessage, streamMessage]
  );

  const handleSetPrivateModelId = useCallback((privateModelId: string) => {
    setSettings((old) => {
      const updated = new ThreadSettingsModel(old._props);
      updated.privateModelId = privateModelId;
      return updated;
    });
  }, []);

  const handleSetAgentId = useCallback((agentId: string) => {
    setSettings((old) => {
      const updated = new ThreadSettingsModel(old._props);
      updated.agentId = agentId;

      if (!updated.isDefaultAgent) {
        updated.togglePublicSearch(false);
      }

      return updated;
    });
  }, []);

  const handleTogglePublicSearch = useCallback((enabled: boolean) => {
    setSettings((old) => {
      const updated = new ThreadSettingsModel(old._props);
      updated.togglePublicSearch(enabled);
      return updated;
    });
  }, []);

  const handleToggleDatasource = useCallback(
    (datasourceId: string | string[], enabled: boolean) => {
      setSettings((old) => {
        const updated = new ThreadSettingsModel(old._props);
        updated.toggleDatasource(datasourceId, enabled);
        return updated;
      });
    },
    []
  );

  // run once on boot
  useEffect(() => {
    if (!signedIn) {
      return;
    }
    if (loadState !== 'unloaded') {
      return;
    }
    setLoadState('loading');
  }, [signedIn, loadState]);

  // load the active thread and set the context load state to loaded
  useEffect(() => {
    if (loadState !== 'loading' || !userThreadSettings.loaded) {
      return;
    }

    const threadId = getUrlThreadId();

    if (!threadId) {
      syncFromUserSettings(qsAgentId);
      setThreadLoadState('loaded');
      setLoadState('loaded');
      return;
    }

    getThreadById(threadId).then((activeThread) => {
      if (activeThread) {
        setThread(activeThread);
        syncFromThread(activeThread);
      } else {
        // @todo error toast this, thread was not found
        history.pushState({}, '', '/chat');
      }

      setThreadLoadState('loaded');
      setLoadState('loaded');
    });
  }, [loadState, syncFromThread, syncFromUserSettings, userThreadSettings.loaded, qsAgentId]);

  // ensure the thread id URL param matches the active thread
  useEffect(() => {
    if (loadState !== 'loaded' || !canViewHistory) {
      return;
    }

    if (!String(window.location.pathname).startsWith('/chat')) {
      return;
    }

    const { id, latestUserMessage } = thread;
    const threadId = getUrlThreadId();

    if (!latestUserMessage) {
      if (threadId) {
        history.pushState({}, '', '/chat');
      }
      return;
    }

    if (threadId !== id) {
      history.pushState({}, '', `/chat/${id}`);
    }
  }, [thread, loadState, canViewHistory]);

  // ensure that the working flag is turned off if there is no on-deck shortcut
  useEffect(() => {
    const onDeckShortcut = thread.conversation.some(({ onDeck, shortcut }) => onDeck && !!shortcut);
    if (!onDeckShortcut) {
      setWorking(false);
    }
  }, [thread.conversation]);

  return (
    <ThreadContext.Provider
      value={{
        loadState: threadLoadState,
        thread,
        settings,
        sendMessage,
        controlSignal,
        status: messageStatus,
        working,
        streamContent,
        setWorking,
        loadShortcut,
        loadThread,
        addMessage,
        sendDeckMessage,
        removeDeckMessage,
        closeStream,
        setPrivateModelId: handleSetPrivateModelId,
        setAgentId: handleSetAgentId,
        togglePublicSearch: handleTogglePublicSearch,
        toggleDatasource: handleToggleDatasource,
      }}
    >
      {children}
    </ThreadContext.Provider>
  );
};
